"use strict";
/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.updateReportSchema = exports.updateReportForm = exports.createReportSchema = exports.createReportParameters = exports.reportParameterArray = void 0;
const parameter_specs_1 = require("@karya/parameter-specs");
const dbschema_1 = require("@karya/dbschema");
const Index_1 = require("../reports/Index");
// Report manager roles (PROJECT_MANAGER, TECH_SUPPORT) are intentionally
// excluded: they can already see all reports within their project, so selecting
// them as an allowed role is moot.
const allowedRolesList = Object.fromEntries(dbschema_1.projectRoles.filter((r) => !Index_1.reportManagerRoles.includes(r)).map((r) => [r, r]));
/** UI/API still treats graph reports separately, so exclude here. */
const reportKindsWithoutGraphs = dbschema_1.ReportKinds.filter((kind) => kind !== 'graphs');
exports.reportParameterArray = [
    {
        id: 'kind',
        label: 'Report kind',
        description: 'Category of the report',
        type: 'enum',
        list: Object.fromEntries(reportKindsWithoutGraphs.map((k) => [k, k])),
        required: true,
    },
    {
        id: 'name',
        label: 'Report Name',
        description: 'Human-readable report name',
        type: 'string',
        required: true,
    },
    {
        id: 'type',
        label: 'Type',
        description: 'Static or dynamic report',
        type: 'enum',
        list: { static: 'static', dynamic: 'dynamic' },
        required: true,
    },
    {
        id: 'sql_query',
        label: 'SQL query',
        description: 'SQL query for the report',
        type: 'string',
        required: true,
        long: true,
        pattern: '^[\\s\\S]*$',
        maxLength: 100000,
        stripHtml: false,
    },
    {
        // Must be declared before any `config.*` field because
        // `config.allowed_project_ids` uses Joi.when('/is_public', ...). Joi resolves
        // that cross-key reference in schema-key insertion order; if `config.*` is
        // inserted first, `is_public`'s .default(false) has not yet been applied
        // when the `when` is evaluated, so the `required()` check on the condition
        // fails and the field is silently stripped.
        id: 'is_public',
        label: 'Public',
        description: 'Whether the report is public',
        type: 'boolean',
        required: false,
    },
    {
        // @ts-expect-error - Path syntax for nested config.query_parameters is an acceptable format for parameter spec
        id: 'config.query_parameters',
        label: 'Query parameters',
        description: 'Optional query parameters (array of { name: string, type: string, value?: any, comments?: string })',
        type: 'json_array',
        renderer: 'report-query-parameters',
        required: false,
    },
    {
        // @ts-expect-error - Path syntax for nested config.allowed_roles is an acceptable format for parameter spec
        id: 'config.allowed_roles',
        label: 'Allowed roles',
        description: 'Project Roles that can access this report',
        type: 'enum_multi',
        list: allowedRolesList,
        required: false,
    },
    {
        // @ts-expect-error - Path syntax for nested config.allowed_project_ids is an acceptable format for parameter spec
        id: 'config.allowed_project_ids',
        label: 'Allowed project IDs',
        description: 'Project IDs for scoped access',
        type: 'list',
        required: false,
        requires: [['is_public', true, 'NOT_EQUALS']],
        requiredIfSatisfied: true,
    },
];
/**
 * Parameter spec for create-report form/API. Base type is InsertableReports.
 * Path syntax (e.g. config.allowed_roles) is used for nested DB-shaped fields.
 * Use createReportSchema for validation. Frontend can reuse for forms.
 */
exports.createReportParameters = [
    {
        label: 'Create Report',
        description: 'Create a new report',
        required: true,
        parameters: exports.reportParameterArray,
    },
];
/**
 * Validation schema for create-report requests.
 * Derived directly from `createReportParameters` to avoid drift.
 */
exports.createReportSchema = (0, parameter_specs_1.joiSchema)(exports.createReportParameters.flatMap((section) => section.parameters), { isKeyPath: true }).unknown(false);
const updateReportParameterArray = exports.reportParameterArray
    .filter((p) => p.id !== 'kind')
    .map((p) => (Object.assign(Object.assign({}, p), { required: false })));
/**
 * Parameter form for updating report
 */
exports.updateReportForm = [
    {
        label: 'Report Update Fields',
        description: 'Editable fields for updating a report. Supplying one or more will update those fields.',
        required: false,
        parameters: updateReportParameterArray,
    },
];
/**
 * Joi schema for update-report request body.
 * Validates to Partial<ReportsRecord> (only updatable fields); rejects unknown keys.
 */
exports.updateReportSchema = (0, parameter_specs_1.joiSchema)(
// Parameter array extracted from form definition
exports.updateReportForm.flatMap((section) => section.parameters), { isKeyPath: true }).unknown(false);
//# sourceMappingURL=report.js.map